import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Mail, Phone, Clock, Send, User, CheckCircle, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { COMPANY_INFO } from '../data/companyData';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Success Modal
const SuccessModal = ({ message, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
  >
    <motion.div
      initial={{ scale: 0.5 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.5 }}
      onClick={(e) => e.stopPropagation()}
      className="bg-[#0A0A0A] border border-[#D91616]/50 p-12 text-center max-w-md mx-4"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: "spring" }}
        className="w-20 h-20 mx-auto mb-6 bg-[#D91616] rounded-full flex items-center justify-center"
      >
        <CheckCircle className="w-10 h-10 text-white" />
      </motion.div>
      <h3 className="text-2xl font-bold text-white mb-2">{message}</h3>
      <p className="text-zinc-400 mb-6">We'll get back to you soon!</p>
      <button onClick={onClose} className="bg-[#D91616] text-white px-8 py-3 font-semibold uppercase tracking-wider">
        Continue
      </button>
    </motion.div>
  </motion.div>
);

const ContactPage = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('contact');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [contactForm, setContactForm] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });

  const [applicationForm, setApplicationForm] = useState({
    full_name: '', email: '', phone: '', category: '', social_media: '', portfolio_url: '', achievements: '', message: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...contactForm, language });
      setSuccessMessage(language === 'es' ? '¡Mensaje enviado!' : 'Message sent!');
      setShowSuccess(true);
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, { ...applicationForm, language });
      setSuccessMessage(language === 'es' ? '¡Aplicación enviada!' : 'Application sent!');
      setShowSuccess(true);
      setApplicationForm({ full_name: '', email: '', phone: '', category: '', social_media: '', portfolio_url: '', achievements: '', message: '' });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {showSuccess && <SuccessModal message={successMessage} onClose={() => setShowSuccess(false)} />}
      </AnimatePresence>

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D91616] mb-4 block">
              {language === 'es' ? 'Contacto' : 'Contact'}
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-white mb-6">
              {language === 'es' ? 'HABLEMOS DE TU FUTURO' : "LET'S TALK ABOUT YOUR FUTURE"}
            </h1>
            <p className="text-xl text-zinc-400">
              {language === 'es'
                ? 'Estamos listos para impulsar tu carrera al siguiente nivel. Contáctanos o aplica para representación.'
                : 'We are ready to take your career to the next level. Contact us or apply for representation.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-[#050505] border border-white/10 p-6">
              <div className="w-12 h-12 bg-[#D91616]/10 flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-[#D91616]" />
              </div>
              <h3 className="text-white font-semibold mb-2">{language === 'es' ? 'Oficina Central' : 'Head Office'}</h3>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.address.street}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.address.floor}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.address.colony}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.address.city}, {COMPANY_INFO.address.zip}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="bg-[#050505] border border-white/10 p-6">
              <div className="w-12 h-12 bg-[#D91616]/10 flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-[#D91616]" />
              </div>
              <h3 className="text-white font-semibold mb-2">{language === 'es' ? 'Teléfonos' : 'Phone'}</h3>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.contact.phone}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.contact.phoneAlt}</p>
              <p className="text-[#D91616] text-sm mt-2">WhatsApp: {COMPANY_INFO.contact.whatsapp}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="bg-[#050505] border border-white/10 p-6">
              <div className="w-12 h-12 bg-[#D91616]/10 flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-[#D91616]" />
              </div>
              <h3 className="text-white font-semibold mb-2">Email</h3>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.contact.email}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.contact.emailTalent}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.contact.emailPress}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="bg-[#050505] border border-white/10 p-6">
              <div className="w-12 h-12 bg-[#D91616]/10 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-[#D91616]" />
              </div>
              <h3 className="text-white font-semibold mb-2">{language === 'es' ? 'Horario' : 'Hours'}</h3>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.hours.weekdays}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.hours.saturday}</p>
              <p className="text-zinc-500 text-sm">{COMPANY_INFO.hours.sunday}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Forms */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6">
          {/* Tab Switcher */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-[#0A0A0A] border border-white/10 p-1">
              <button
                onClick={() => setActiveTab('contact')}
                className={`px-8 py-3 text-sm font-semibold uppercase tracking-wider transition-all ${
                  activeTab === 'contact' ? 'bg-[#D91616] text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                {language === 'es' ? 'Contacto General' : 'General Contact'}
              </button>
              <button
                onClick={() => setActiveTab('apply')}
                className={`px-8 py-3 text-sm font-semibold uppercase tracking-wider transition-all ${
                  activeTab === 'apply' ? 'bg-[#D91616] text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                {language === 'es' ? 'Aplicar para Representación' : 'Apply for Representation'}
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'contact' ? (
              <motion.form
                key="contact"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleContactSubmit}
                className="bg-[#0A0A0A] border border-white/10 p-8"
              >
                <h2 className="text-xl font-bold text-white mb-6">{language === 'es' ? 'Envíanos un mensaje' : 'Send us a message'}</h2>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Nombre completo' : 'Full name'} *</label>
                    <Input required value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} className="bg-transparent border-white/20 text-white rounded-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Email *</label>
                    <Input type="email" required value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} className="bg-transparent border-white/20 text-white rounded-none" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Teléfono' : 'Phone'}</label>
                    <Input type="tel" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} className="bg-transparent border-white/20 text-white rounded-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Asunto' : 'Subject'} *</label>
                    <Input required value={contactForm.subject} onChange={(e) => setContactForm({...contactForm, subject: e.target.value})} className="bg-transparent border-white/20 text-white rounded-none" />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Mensaje' : 'Message'} *</label>
                  <Textarea required rows={5} value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} className="bg-transparent border-white/20 text-white rounded-none resize-none" />
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-[#D91616] hover:bg-[#ED1C24] text-white py-4 rounded-none font-semibold uppercase tracking-wider">
                  {loading ? '...' : <><Send className="w-4 h-4 mr-2" />{language === 'es' ? 'Enviar Mensaje' : 'Send Message'}</>}
                </Button>
              </motion.form>
            ) : (
              <motion.form
                key="apply"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleApplicationSubmit}
                className="bg-[#0A0A0A] border border-white/10 p-8"
              >
                <h2 className="text-xl font-bold text-white mb-2">{language === 'es' ? 'Aplica para Representación' : 'Apply for Representation'}</h2>
                <p className="text-zinc-500 text-sm mb-6">{language === 'es' ? 'Completa el formulario y nuestro equipo evaluará tu perfil.' : 'Complete the form and our team will evaluate your profile.'}</p>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Nombre completo' : 'Full name'} *</label>
                    <Input required value={applicationForm.full_name} onChange={(e) => setApplicationForm({...applicationForm, full_name: e.target.value})} className="bg-transparent border-white/20 text-white rounded-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">Email *</label>
                    <Input type="email" required value={applicationForm.email} onChange={(e) => setApplicationForm({...applicationForm, email: e.target.value})} className="bg-transparent border-white/20 text-white rounded-none" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Teléfono' : 'Phone'} *</label>
                    <Input type="tel" required value={applicationForm.phone} onChange={(e) => setApplicationForm({...applicationForm, phone: e.target.value})} className="bg-transparent border-white/20 text-white rounded-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Categoría' : 'Category'} *</label>
                    <Select value={applicationForm.category} onValueChange={(value) => setApplicationForm({...applicationForm, category: value})}>
                      <SelectTrigger className="bg-transparent border-white/20 text-white rounded-none">
                        <SelectValue placeholder={language === 'es' ? 'Selecciona...' : 'Select...'} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/20">
                        <SelectItem value="artist" className="text-white">{language === 'es' ? 'Artista' : 'Artist'}</SelectItem>
                        <SelectItem value="athlete" className="text-white">{language === 'es' ? 'Deportista' : 'Athlete'}</SelectItem>
                        <SelectItem value="influencer" className="text-white">Influencer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Redes Sociales' : 'Social Media'}</label>
                    <Input value={applicationForm.social_media} onChange={(e) => setApplicationForm({...applicationForm, social_media: e.target.value})} placeholder="@username" className="bg-transparent border-white/20 text-white rounded-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Portafolio/Web' : 'Portfolio/Web'}</label>
                    <Input type="url" value={applicationForm.portfolio_url} onChange={(e) => setApplicationForm({...applicationForm, portfolio_url: e.target.value})} placeholder="https://" className="bg-transparent border-white/20 text-white rounded-none" />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Logros y Trayectoria' : 'Achievements and Career'} *</label>
                  <Textarea required rows={4} value={applicationForm.achievements} onChange={(e) => setApplicationForm({...applicationForm, achievements: e.target.value})} className="bg-transparent border-white/20 text-white rounded-none resize-none" placeholder={language === 'es' ? 'Cuéntanos sobre tus logros más importantes...' : 'Tell us about your most important achievements...'} />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-zinc-400 mb-2">{language === 'es' ? 'Mensaje adicional' : 'Additional message'}</label>
                  <Textarea rows={3} value={applicationForm.message} onChange={(e) => setApplicationForm({...applicationForm, message: e.target.value})} className="bg-transparent border-white/20 text-white rounded-none resize-none" />
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-[#D91616] hover:bg-[#ED1C24] text-white py-4 rounded-none font-semibold uppercase tracking-wider">
                  {loading ? '...' : <><Send className="w-4 h-4 mr-2" />{language === 'es' ? 'Enviar Aplicación' : 'Submit Application'}</>}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Map placeholder */}
      <section className="h-96 bg-zinc-900 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-[#D91616] mx-auto mb-4" />
            <p className="text-white font-semibold">{COMPANY_INFO.address.street}</p>
            <p className="text-zinc-400">{COMPANY_INFO.address.colony}, {COMPANY_INFO.address.city}</p>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
